/**
 * WeaponsServiceModule — 2026-06-08 Phase 1D wire-up for the canonical
 * Q Manual VG35002 weapons catalog REST surface.
 *
 * Wraps the briefing-room backend's weapons-library endpoints (authored
 * in Phase 1C, commit 7092f93c1). Lets the SPA list every weapon with
 * compact-shape metadata and fetch a single entry's full data (stats,
 * description, Q evaluation, game information, special rules).
 *
 * Routes (per briefing_room_backend/routes/weapons.py):
 *   GET /api/modules/briefing-room/weapons              → WeaponsManifest
 *   GET /api/modules/briefing-room/weapons/{weapon_id}  → full weapon JSON | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders() (mirrors
 * ModulesServiceModule.ts).
 *
 * Plan reference: docs/20260608/20260608_PLAN_Q_MANUAL_INTEGRATION_INTO_BRIEFING_ROOM.md
 * §0R.4 (1D scope).
 */
import { getAuthHeaders } from './authHeaders';

/** Allowed weapon category values per the seed schema (5 categories). */
export type WeaponCategory =
  | 'pistol'
  | 'rifle_smg_shotgun'
  | 'miscellaneous_modern_weapon'
  | 'weapons_accessory'
  | 'archaic_weapon';

/** Per-weapon summary returned by the manifest endpoint. */
export interface WeaponManifestEntry {
  id: string;
  name: string;
  category: WeaponCategory;
  page: number | null;
  manifest_section: string | null;
}

export interface WeaponsManifest {
  weapons: WeaponManifestEntry[];
  total: number;
  categories_complete: string[];
  categories_pending: string[];
  schema_version: string;
  source: string;
}

const BASE_PATH = '/api/modules/briefing-room/weapons';

export class WeaponsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  /** List all weapons with compact metadata. */
  async list(): Promise<WeaponsManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`WeaponsServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as WeaponsManifest;
  }

  /**
   * Fetch a single weapon's full entry. Returns null on 404 so callers
   * can branch on missing-vs-error.
   */
  async get(weaponId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(weaponId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`WeaponsServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
